import type { EdgeBucketingDef } from './flag-types';

/**
 * The bucketing inputs for every edge experiment — and the ONLY flags module
 * the CloudFront viewer-request bundle imports.
 *
 * Everything in this file ships to a ≤3,674-byte function
 * (`packages/infra/src/web/edge.ts`), so it holds the four fields
 * `handler-request.ts` actually reads and nothing else. `EdgeClientFlags` in
 * `./edge-client-flags` composes the full `FeatureFlagDef` — `variants`,
 * `default`, `bucketing.mode` — on top of these entries for every non-edge
 * consumer, so there is one source of truth and no duplicated split.
 *
 * The isolation is physical, not conventional: rolldown cannot tree-shake
 * object properties and `@listening/frontend` is not marked
 * `sideEffects: false`, so a field written here is a field the edge pays for
 * whether or not it is read. Same reason `edge-client-flags.ts` was split out
 * of `client-flags.ts`, and `edge-experiment-policies.ts` out of both.
 *
 * **Import NOTHING here.** A value import from another module drags that
 * module's own graph into the bundle. Splits are written as literals for
 * exactly that reason, including `pricingTestV2`'s, which is pinned against
 * `@listening/api-contract`'s copy by `test/bucketing/pricing-split-parity.test.ts`
 * rather than imported from it.
 *
 * Re-measure with `bun run build:edge:request` + `wc -c` in `apps/start` before
 * merging any change to this file.
 */
export const EdgeBucketing = {
  academicLandingFunnelV1: {
    key: 'academic-landing-funnel-v1',
    enrollment: 1,
    split: { control: 0.5, treatment: 0.5 },
    fallbackVariant: 'control',
  },
  homepageCtaFunnelV1: {
    key: 'homepage-cta-funnel-v1',
    enrollment: 1,
    split: { control: 0.5, treatment: 0.5 },
    fallbackVariant: 'control',
  },
  /**
   * Two-arm annual price test, both arms with a 3-day trial, Mode C: the edge
   * cookie assigns the arm and the account pin freezes it
   * (`edge-experiment-policies.ts`). The variants are edge CODES, decoded
   * through `PRICING_TEST_V2_EDGE_CODES` in
   * `@listening/api-contract/pricing-experiment`: `a` is `control` ($39),
   * `b` is `price-59-trial` ($59).
   *
   * `enrollment: 1` — every paywall visitor is a subject, and the visitor's
   * own `/pricing` page must be able to show their arm.
   *
   * `fallbackVariant: 'b'` is the TOP-priced arm — decision C4. Crawlers and
   * cookie-less requests are never advertised a price below what an
   * assignable arm charges.
   *
   * Split matches `PRICING_TEST_V2_SPLIT` exactly (parity-tested).
   */
  pricingTestV2: {
    key: 'pricing-test-v2',
    enrollment: 1,
    split: { a: 0.5, b: 0.5 },
    fallbackVariant: 'b',
  },
} as const satisfies Record<string, EdgeBucketingDef>;
